/**
 * Knowledge base tags list with drag-and-drop reordering.
 */

import React, { useCallback, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button, Card, Empty, Modal, Spin, message } from 'antd';
import { PlusOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import { useKBTags, useKBArticles, useUpdateTagOrder, useDeleteTag } from '../hooks/useKnowledgeBase';
import type { KBTag } from '@municipality/types';
import { SortableTagRow, SortableKbList } from '../components/knowledgeBase';

export const KnowledgeBaseTags: React.FC = () => {
  const { t } = useTranslation(['knowledgeBase', 'common']);
  const navigate = useNavigate();
  const [orderedIds, setOrderedIds] = useState<string[]>([]);

  const { data: tags, isLoading } = useKBTags();
  const { data: articles } = useKBArticles();
  const updateOrderMutation = useUpdateTagOrder();
  const deleteMutation = useDeleteTag();

  const articleCountByTag = useMemo(() => {
    const counts: Record<string, number> = {};
    articles?.forEach((a) => {
      a.tagIds?.forEach((tagId) => {
        counts[tagId] = (counts[tagId] ?? 0) + 1;
      });
    });
    return counts;
  }, [articles]);

  const displayOrder = useMemo(() => {
    if (orderedIds.length > 0) return orderedIds;
    return tags?.map((tagItem) => tagItem.id) ?? [];
  }, [tags, orderedIds]);

  const handleReorder = useCallback(
    async (newOrder: string[]) => {
      setOrderedIds(newOrder);
      try {
        await updateOrderMutation.mutateAsync(newOrder);
        message.success(t('knowledgeBase:messages.orderUpdated'));
      } catch {
        message.error(t('knowledgeBase:errors.saveError'));
        setOrderedIds([]);
      }
    },
    [updateOrderMutation, t]
  );

  const handleEdit = useCallback(
    (tag: { id: string }) => {
      navigate(`/knowledge-base/tags/edit/${tag.id}`);
    },
    [navigate]
  );

  const handleDelete = useCallback(
    (tag: KBTag) => {
      const name =
        tag.translations?.[tag.languageSettings?.primaryLanguage ?? 'en']?.name ??
        tag.translations?.en?.name ??
        tag.id;
      Modal.confirm({
        title: t('knowledgeBase:messages.confirmDelete'),
        content: t('knowledgeBase:tags.deleteConfirm', { name }),
        okText: t('common:buttons.delete'),
        okType: 'danger',
        cancelText: t('common:buttons.cancel'),
        onOk: async () => {
          try {
            await deleteMutation.mutateAsync(tag.id);
            message.success(t('knowledgeBase:messages.tagDeleted'));
          } catch {
            message.error(t('knowledgeBase:errors.deleteError'));
          }
        },
      });
    },
    [deleteMutation, t]
  );

  if (isLoading) {
    return (
      <div style={{ textAlign: 'center', padding: 48 }}>
        <Spin size="large" />
      </div>
    );
  }

  return (
    <div>
      <div style={{ marginBottom: 24, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <h2 style={{ margin: 0 }}>{t('knowledgeBase:tags.title')}</h2>
        <Button
          type="primary"
          icon={<PlusOutlined />}
          onClick={() => navigate('/knowledge-base/tags/create')}
        >
          {t('knowledgeBase:tags.create')}
        </Button>
      </div>

      <Card>
        {!tags?.length ? (
          <Empty
            description={t('knowledgeBase:tags.noTags')}
            image={Empty.PRESENTED_IMAGE_SIMPLE}
          >
            <Button
              type="primary"
              icon={<PlusOutlined />}
              onClick={() => navigate('/knowledge-base/tags/create')}
            >
              {t('knowledgeBase:tags.create')}
            </Button>
          </Empty>
        ) : (
          <SortableKbList itemIds={displayOrder} onReorder={handleReorder}>
            <div style={{ maxHeight: 600, overflowY: 'auto' }}>
              {displayOrder.map((tagId) => {
                const tag = tags?.find((tagItem) => tagItem.id === tagId);
                if (!tag) return null;
                return (
                  <SortableTagRow
                    key={tag.id}
                    tag={tag}
                    articleCount={articleCountByTag[tag.id] ?? 0}
                    onEdit={handleEdit}
                    onDelete={handleDelete}
                  />
                );
              })}
            </div>
          </SortableKbList>
        )}
      </Card>
    </div>
  );
};
